import { useSessionMessageApi } from '../../utils/actionableNotifications';
import React, { createContext, useContext, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, Modal, Typography } from 'antd';

export const SettingsHeaderActionsContext = createContext(null);
const SaveRegistryContext = createContext(null);

export function SettingsSaveGroup({ children, target, onDirtyChange }) {
  const message = useSessionMessageApi();
  const [actions, setActions] = useState({});
  const [saving, setSaving] = useState(false);
  const flight = useRef(false);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const registry = useMemo(() => ({
    register: (id, action) => setActions(current => ({ ...current, [id]: action })),
    remove: id => setActions(current => { const next = { ...current }; delete next[id]; return next; }),
  }), []);
  const entries = Object.values(actions);
  const saveEntries = entries.filter(item => item.saveable);
  const dirty = entries.some(item => item.dirty);
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);
  const save = async () => {
    if (flight.current) return;
    flight.current = true; setSaving(true);
    try {
      // Freeze this click's changed actions. Never replay a failed save.
      const handlers = saveEntries.filter(item => item.dirty !== false && !item.disabled && !item.loading).map(item => item.button.current.props.onClick);
      for (const handler of handlers) {
        if (!alive.current) break;
        if (await handler?.() === false) break;
      }
    } catch (error) {
      if (alive.current) message.error(error.message || 'Unable to save settings. Review the affected section before trying again.');
    } finally { flight.current = false; if (alive.current) setSaving(false); }
  };
  const grouped = saveEntries.length > 1;
  return <SaveRegistryContext.Provider value={registry}>{children}{target && createPortal(
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{dirty && <Typography.Text type="warning">Unsaved changes</Typography.Text>}{grouped && <Button type="primary" loading={saving || saveEntries.some(item => item.loading)} disabled={saving || !saveEntries.some(item => item.dirty !== false && !item.disabled && !item.loading)} onClick={save}>Save changes</Button>}
      {entries.filter(item => !grouped || !item.saveable).map(item => <React.Fragment key={item.id}>{React.cloneElement(item.button.current, { onClick: () => item.button.current.props.onClick?.() })}</React.Fragment>)}</div>, target)}
  </SaveRegistryContext.Provider>;
}

// Explicit action slot: save handlers and their state stay owned by each panel.
// Outside the settings page (including editor dialogs), keep the original layout.
export default function SettingsHeaderActions({ children, label, dirty, confirmOnSave }) {
  const target = useContext(SettingsHeaderActionsContext);
  const registry = useContext(SaveRegistryContext);
  const id = useId();
  const [modal, holder] = Modal.useModal();
  const flight = useRef(false), alive = useRef(true), dialog = useRef(null);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; dialog.current?.destroy(); dialog.current?.cancel(); };
  }, []);
  const invoke = async (...args) => {
    if (flight.current) return false;
    flight.current = true;
    try {
      const options = typeof confirmOnSave === 'function' ? confirmOnSave() : confirmOnSave;
      if (options) {
        const confirmed = await new Promise(resolve => {
          const instance = modal.confirm({ ...options, onOk: () => resolve(true), onCancel: () => resolve(false) });
          dialog.current = { destroy: instance.destroy, cancel: () => resolve(false) };
        });
        dialog.current = null;
        if (!confirmed || !alive.current) return false;
      }
      return await children.props.onClick?.(...args);
    } finally { flight.current = false; }
  };
  const action = confirmOnSave && React.isValidElement(children) ? React.cloneElement(children, { onClick: invoke }) : children;
  const button = useRef(children);
  button.current = action;
  const registerable = target && registry && React.isValidElement(children)
    && typeof children.props.onClick === 'function';
  const saveable = /save/i.test(String(children?.props?.children));
  const disabled = Boolean(children?.props?.disabled);
  const loading = Boolean(children?.props?.loading);
  useEffect(() => {
    if (!registerable) return;
    registry.register(id, { id, button, disabled, loading, dirty, saveable });
    return () => registry.remove(id);
  }, [registerable, registry, id, disabled, loading, dirty, saveable]);
  if (registerable) return holder;
  const content = <>{dirty && <Typography.Text type="warning">Unsaved changes</Typography.Text>}{action}</>;
  return <>{holder}{target ? createPortal(<div className="global-settings-action-group">{label && <span className="global-settings-action-label">{label}</span>}{content}</div>, target) : content}</>;
}
